import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';

/** Hostnames treated as local development for gating dev-only UI. */
const LOCAL_HOSTNAMES = ['localhost'];

/**
 * Whether the app is currently running on a local dev host. Used to gate UI
 * (e.g. social login buttons) that depends on OAuth redirect URIs only
 * registered for localhost, not the deployed domains.
 *
 * Takes `hostname` as a parameter (defaulting to the real one) instead of
 * reading `window.location` internally, since `window.location` isn't
 * configurable and so can't be mocked with `spyOnProperty` in tests.
 */
export function isLocalhost(hostname: string = window.location.hostname): boolean {
  return LOCAL_HOSTNAMES.includes(hostname);
}

/**
 * Whether signup and social login are available on the current host. Only the
 * hostnames in environment.redirectDomainUrl have OAuth redirect URIs registered,
 * so both the UI (signup link + social buttons) and the /signup routes are gated
 * on this — on any other host signup/social login is hidden and the routes redirect
 * back to /login.
 *
 * The packaged Capacitor app always reports `window.location.hostname` as
 * `localhost` (see capacitor.config.ts's `androidScheme: 'https'`) regardless of
 * which backend/brand it was built for, so a plain hostname check would block
 * signup on every native install. The native app is treated as allowed by default —
 * it doesn't rely on the WebView host having a registered OAuth redirect URI —
 * except on iOS, where the whole registration workflow is hidden: Apple's app
 * review requires any in-app account creation that leads to a paid subscription to
 * go through Apple's In-App Purchase (Guideline 3.1.1), and this app's signup flow
 * ends in a Razorpay checkout instead (see `choose-plan-modal`/`add-on-purchase-modal`
 * and `Menu`-independent flows). Android keeps the native carve-out since it isn't
 * subject to that guideline; the web app is already gated by the hostname allow-list.
 *
 * Takes `hostname`, `isNativeApp`, and `platform` as parameters (defaulting to the
 * real ones) for the same testability reason as isLocalhost() above.
 */
export function isSignupSocialAllowed(
  hostname: string = window.location.hostname,
  isNativeApp: boolean = Capacitor.isNativePlatform(),
  platform: string = Capacitor.getPlatform(),
): boolean {
  if (isNativeApp) return platform !== 'ios';
  return environment.redirectDomainUrl.includes(hostname);
}
